import {useTranslation} from 'react-i18next';

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type {MedicationForm} from '@/services/graphqlHooks';
import {UnitType} from '@/services/graphqlHooks';
import {FORM_SUGGESTED_UNITS} from '@/shared/residents/utils/medicationFormInputs';
import unitType from '@/translation/unitType';
import {localeCompareSort} from '@/utils/localeCompareSort';

export type MedicationUnitSelectProps = {
  onChange: (value: string) => void;
  selectedMedicationForm?: MedicationForm;
  value?: string;
};

const allUnitOptions = Object.values(UnitType);

function sortUnitsByLabel(units: UnitType[]) {
  return [...units].sort((a, b) => localeCompareSort(unitType[a], unitType[b]));
}

function MedicationUnitSelect({
  onChange,
  selectedMedicationForm,
  value,
}: MedicationUnitSelectProps) {
  const {t} = useTranslation();

  const suggestedUnits = selectedMedicationForm
    ? FORM_SUGGESTED_UNITS[selectedMedicationForm] ?? []
    : [];
  const sortedSuggestedUnits = sortUnitsByLabel(suggestedUnits);
  const otherUnits = sortUnitsByLabel(
    allUnitOptions.filter(unit => !suggestedUnits.includes(unit))
  );

  return (
    <Select onValueChange={onChange} value={value ?? ''}>
      <SelectTrigger>
        <SelectValue placeholder={t('Select unit')} />
      </SelectTrigger>
      <SelectContent>
        {sortedSuggestedUnits.length > 0 && (
          <SelectGroup>
            <SelectLabel>{t('Suggested')}</SelectLabel>
            {sortedSuggestedUnits.map(unit => (
              <SelectItem key={unit} value={unit}>
                {unitType[unit]}
              </SelectItem>
            ))}
          </SelectGroup>
        )}
        {sortedSuggestedUnits.length > 0 && otherUnits.length > 0 && (
          <SelectSeparator />
        )}
        <SelectGroup>
          {sortedSuggestedUnits.length > 0 && (
            <SelectLabel>{t('Other')}</SelectLabel>
          )}
          {otherUnits.map(unit => (
            <SelectItem key={unit} value={unit}>
              {unitType[unit]}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

export default MedicationUnitSelect;
